<template>
  <div class="auth-manage">
    <!-- 子标签切换 -->
    <div class="auth-tabs">
      <div class="auth-tab" :class="{ active: subTab === 'devices' }" @click="subTab = 'devices'">
        设备管理
      </div>
      <div class="auth-tab" :class="{ active: subTab === 'codes' }" @click="subTab = 'codes'">
        授权码管理
      </div>
    </div>

    <!-- ========== 授权码管理 ========== -->
    <div v-if="subTab === 'codes'" class="auth-section">
      <!-- 创建授权码 -->
      <div class="section-card">
        <div class="section-header">
          <span class="section-title">创建授权码</span>
        </div>
        <div class="create-row">
          <NInput
            v-model:value="newCode"
            placeholder="输入授权码（支持任意字符）"
            size="large"
            @keyup.enter="handleCreateCode"
          />
          <NButton type="primary" size="large" @click="handleCreateCode" :loading="creating">
            创建
          </NButton>
        </div>
      </div>

      <!-- 未使用授权码列表 -->
      <div class="section-card fill">
        <div class="section-header">
          <span class="section-title">未使用授权码</span>
          <span class="section-count">共 {{ codeTotal }} 个</span>
        </div>
        <div class="code-list">
          <div class="code-item" v-for="item in codeList" :key="item.id">
            <div class="code-info">
              <span class="code-value">{{ item.code }}</span>
              <span class="code-time">{{ formatTime(item.create_time) }}</span>
            </div>
            <NPopconfirm @positive-click="handleDeleteCode(item.id!)">
              <template #trigger>
                <NButton size="small" type="error" quaternary>删除</NButton>
              </template>
              确定删除授权码 "{{ item.code }}" 吗？
            </NPopconfirm>
          </div>
          <div v-if="codeList.length === 0" class="empty-tip">暂无未使用的授权码</div>
        </div>
        <div class="pagination-wrap" v-if="codeTotalPages > 1">
          <NPagination
            v-model:page="codePage"
            :page-count="codeTotalPages"
            :page-slot="5"
            size="small"
            @update:page="loadCodeList"
          />
        </div>
      </div>
    </div>

    <!-- ========== 设备管理 ========== -->
    <div v-if="subTab === 'devices'" class="auth-section">
      <!-- 设备列表 -->
      <div class="section-card fill">
        <div class="section-header">
          <span class="section-title">已绑定设备</span>
          <span class="section-count">共 {{ deviceTotal }} 台</span>
        </div>
        <!-- 筛选栏 -->
        <div class="filter-row">
          <div class="filter-item">
            <span class="filter-label">状态</span>
            <NSelect
              v-model:value="deviceStatusFilter"
              :options="statusOptions"
              size="small"
              style="width: 100px"
              @update:value="loadDeviceList(1)"
            />
          </div>
          <div class="filter-item">
            <span class="filter-label">排序</span>
            <NSelect
              v-model:value="deviceSort"
              :options="sortOptions"
              size="small"
              style="width: 140px"
              @update:value="loadDeviceList(1)"
            />
          </div>
        </div>
        <!-- 设备卡片 -->
        <div class="device-list">
          <div class="device-item" v-for="item in deviceList" :key="item.id">
            <div class="device-header">
              <span class="online-dot" :class="deviceStateClass(item)"></span>
              <span class="online-text" :class="deviceStateClass(item)">{{ deviceStateText(item) }}</span>
              <NTag v-if="item.devtools_open" type="error" size="small">调试器已开启</NTag>
              <NTag :type="item.status === 1 ? 'success' : 'error'" size="small">
                {{ item.status === 1 ? '已启用' : '已禁用' }}
              </NTag>
              <span class="device-header-right">
                <span class="device-status-switch">
                  <span class="device-action-label">启用</span>
                  <NSwitch
                    size="small"
                    :value="item.status === 1"
                    @update:value="(val: boolean) => handleToggleStatus(item, val)"
                  />
                </span>
                <button type="button" class="meta-toggle" @click="toggleDeviceExpanded(item)">
                  {{ isDeviceExpanded(item) ? '收起' : '展开全部' }}
                  <span class="meta-toggle-arrow">{{ isDeviceExpanded(item) ? '▴' : '▾' }}</span>
                </button>
              </span>
            </div>
            <div class="device-meta">
              <div class="meta-row">
                <span class="meta-label">授权码</span>
                <span class="meta-value">{{ item.auth_code }}</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">当前页面</span>
                <span v-if="deviceStateClass(item) === 'closed'" class="meta-value">-</span>
                <a
                  v-else-if="item.page_url"
                  class="meta-link"
                  :href="item.page_url"
                  target="_blank"
                  rel="noreferrer"
                  :title="item.page_url"
                >{{ item.page_title || item.page_url }}</a>
                <span v-else class="meta-value">-</span>
              </div>
              <div class="meta-row">
                <span class="meta-label">IP / 地区</span>
                <span class="meta-value">
                  {{ item.ip || '-' }}
                  <template v-if="item.region"> · {{ item.region }}</template>
                  <template v-if="latencyValue(item) >= 0 || latencyLineValue(item) >= 0">
                    ·
                    <span
                      v-if="latencyValue(item) >= 0"
                      class="latency"
                      :class="latencyMainClass(item)"
                      title="主地址：WebSocket 往返"
                    >{{ latencyLineValue(item) >= 0 ? '主 ' : '' }}{{ latencyValue(item) }}ms</span>
                    <template v-if="latencyValue(item) >= 0 && latencyLineValue(item) >= 0"> · </template>
                    <span
                      v-if="latencyLineValue(item) >= 0"
                      class="latency"
                      :class="latencyLineClass(item)"
                      title="线路：到线路域名的 HTTP 首字节耗时（含 CDN 鉴权/回源，通常大于 ping 值；ping 只测网络往返）"
                    >线 {{ latencyLineValue(item) }}ms</span>
                  </template>
                  <button
                    type="button"
                    class="lat-test"
                    :class="{ busy: latencyTestingId === item.device_id }"
                    :disabled="deviceStateClass(item) !== 'on' || latencyTestingId === item.device_id"
                    title="立即测速"
                    @click.stop="handleTestLatency(item)"
                  >{{ latencyTestingId === item.device_id ? '测速中' : '测速' }}</button>
                </span>
              </div>
              <template v-if="isDeviceExpanded(item)">
                <div class="meta-row">
                  <span class="meta-label">打开页面</span>
                  <span v-if="deviceStateClass(item) === 'closed'" class="meta-closed-tag">浏览器已关闭</span>
                  <span v-else class="meta-value">{{ item.open_tab_count || 0 }} 个</span>
                </div>
                <div class="meta-row">
                  <span class="meta-label">调试器</span>
                  <span class="meta-value" :class="{ danger: item.devtools_open }">
                    {{ item.devtools_open ? '已开启' : '未开启' }}
                    <template v-if="item.devtools_count">（累计 {{ item.devtools_count }} 次）</template>
                  </span>
                </div>
                <div class="meta-row">
                  <span class="meta-label">最后心跳</span>
                  <span class="meta-value">{{ formatTime(item.last_seen_time) }}</span>
                </div>
                <div class="meta-row">
                  <span class="meta-label">绑定时间</span>
                  <span class="meta-value">{{ formatTime(item.bind_time) }}</span>
                </div>
              </template>
            </div>
            <div class="device-ops">
              <NButton
                size="small"
                secondary
                type="primary"
                :loading="screenshotLoadingId === item.device_id"
                :disabled="deviceStateClass(item) !== 'on'"
                @click="handleCaptureScreenshot(item)"
              >拍照</NButton>
              <NPopconfirm @positive-click="handleDeleteDevice(item.id!)">
                <template #trigger>
                  <NButton class="ops-delete" size="small" secondary type="error">删除</NButton>
                </template>
                确定删除该设备吗？
                <br />
                删除后该设备将无法使用插件。
              </NPopconfirm>
              <template v-if="isDeviceExpanded(item)">
                <NButton size="small" secondary @click="openHistory(item)">历史记录</NButton>
                <NButton size="small" secondary @click="openScreenshotList(item)">截图记录</NButton>
                <NPopconfirm @positive-click="handleReloadBrowser(item)">
                  <template #trigger>
                    <NButton size="small" secondary type="warning" :disabled="deviceStateClass(item) !== 'on'">刷新浏览器</NButton>
                  </template>
                  确定让该设备刷新所有标签页吗？
                </NPopconfirm>
                <NPopconfirm @positive-click="handleCloseBrowser(item)">
                  <template #trigger>
                    <NButton size="small" secondary type="error" :disabled="deviceStateClass(item) !== 'on'">关闭浏览器</NButton>
                  </template>
                  确定让该设备关闭浏览器吗？关闭后需要用户手动重新打开。
                </NPopconfirm>
              </template>
            </div>
          </div>
          <div v-if="deviceList.length === 0" class="empty-tip">暂无绑定设备</div>
        </div>
        <div class="pagination-wrap" v-if="deviceTotalPages > 1">
          <NPagination
            v-model:page="devicePage"
            :page-count="deviceTotalPages"
            :page-slot="5"
            size="small"
            @update:page="loadDeviceList"
          />
        </div>
      </div>
    </div>

    <!-- ========== 设备历史记录（全屏） ========== -->
    <NDrawer
      v-model:show="showHistory"
      placement="right"
      width="100vw"
      :mask-closable="false"
      :close-on-esc="true"
    >
      <NDrawerContent
        title="设备历史记录"
        closable
        :body-style="{ overflow: 'hidden' }"
        :body-content-style="{
          height: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
        }"
      >
        <div class="history-wrap" v-if="historyDevice">
          <!-- 设备信息 -->
          <div class="history-head">
            <div class="history-head-main">
              <span class="history-online-dot" :class="deviceStateClass(historyDevice)"></span>
              <span class="history-online-text" :class="deviceStateClass(historyDevice)">{{ deviceStateText(historyDevice) }}</span>
              <span class="history-device" :title="historyDevice.device_id">{{ historyDevice.device_id }}</span>
              <span class="history-meta">
                {{ historyDevice.ip || '-' }}
                <template v-if="historyDevice.region"> · {{ historyDevice.region }}</template>
              </span>
              <span class="history-meta" v-if="historyDevice.last_seen_time">
                最后心跳 {{ formatTime(historyDevice.last_seen_time) }}
              </span>
              <span class="history-meta" v-if="deviceStateClass(historyDevice) === 'closed'">浏览器已关闭</span>
              <span class="history-meta" v-else>当前打开 {{ historyTabs.length }} 个页面</span>
            </div>
            <div class="history-head-ops">
              <NButton size="small" quaternary :loading="historyLoading" @click="loadHistory(historyPage, historyPageSize)">
                刷新
              </NButton>
            </div>
          </div>

          <div class="history-main">
            <!-- 当前打开的页面 -->
            <aside class="history-side" v-if="historyTabs.length">
              <div class="history-side-title">
                <span>当前打开的页面（{{ historyTabs.length }}）</span>
                <span class="history-side-hint">高亮 = 正在浏览</span>
              </div>
              <div class="history-side-list">
                <div
                  class="history-tab"
                  :class="{ active: isCurrentTab(t) }"
                  v-for="(t, i) in sortedHistoryTabs"
                  :key="`${t.url}-${i}`"
                >
                  <div class="history-tab-head">
                    <a :href="t.url" target="_blank" rel="noreferrer" :title="t.title || t.url">{{ t.title || t.url }}</a>
                    <span v-if="isCurrentTab(t)" class="history-tab-badge">当前</span>
                  </div>
                  <span class="history-tab-url" :title="t.url">{{ t.url }}</span>
                </div>
              </div>
            </aside>

            <!-- 操作历史 -->
            <section class="history-content">
              <!-- 事件分类：把浏览页面 / 上下线 / 调试器 / 遥控指令 / 测速分开，避免全堆在一张列表里 -->
              <div class="history-filters">
                <button
                  v-for="g in historyGroups"
                  :key="g.key"
                  type="button"
                  class="history-filter"
                  :class="{ active: historyFilter === g.key }"
                  @click="handleHistoryFilter(g.key)"
                >
                  {{ g.label }}
                  <span class="history-filter-count">{{ historyGroupCounts[g.key] || 0 }}</span>
                </button>
              </div>

              <div class="history-cols">
                <span class="col-time">时间</span>
                <span class="col-event">事件</span>
                <span class="col-body">页面 / 内容</span>
                <span class="col-duration">停留</span>
                <span class="col-ip">IP / 地区</span>
              </div>

              <div class="history-list">
                <div class="history-item" v-for="h in historyList" :key="h.id">
                  <span class="history-time">{{ formatTime(h.start_time) }}</span>
                  <span class="history-badge" :class="'t-' + h.event_type">{{ eventLabel(h.event_type) }}</span>
                  <div class="history-body">
                    <a
                      v-if="h.event_type === 'page' && h.url"
                      class="history-link"
                      :href="h.url"
                      target="_blank"
                      rel="noreferrer"
                      :title="h.url"
                    >{{ h.title || h.url }}</a>
                    <span v-else class="history-text">{{ h.url ? (h.title || h.url) : '-' }}</span>
                  </div>
                  <span class="history-duration">{{ h.duration != null ? formatDuration(h.duration) : '-' }}</span>
                  <span class="history-ip" :title="h.ip || ''">
                    {{ h.ip || '-' }}
                    <template v-if="h.region"> · {{ h.region }}</template>
                  </span>
                </div>
                <div v-if="historyLoading && historyList.length === 0" class="history-empty">加载中…</div>
                <div v-else-if="historyList.length === 0" class="history-empty">暂无记录</div>
              </div>

              <div class="history-footer">
                <span class="history-total">共 {{ historyTotal }} 条</span>
                <NPagination
                  :page="historyPage"
                  :page-size="historyPageSize"
                  :item-count="historyTotal"
                  :page-sizes="[20, 50, 100, 200]"
                  :page-slot="7"
                  show-size-picker
                  show-quick-jumper
                  size="small"
                  @update:page="handleHistoryPageChange"
                  @update:page-size="handleHistoryPageSizeChange"
                />
              </div>
            </section>
          </div>
        </div>
      </NDrawerContent>
    </NDrawer>

    <!-- 设备截图记录（最近 20 张，可直接点开查看） -->
    <NModal
      v-model:show="shotListVisible"
      preset="card"
      title="截图记录"
      style="width: 1120px; max-width: 96vw"
    >
      <div v-if="shotListLoading" class="shot-empty">加载中…</div>
      <div v-else-if="shotList.length === 0" class="shot-empty">
        暂无截图。点击设备卡片上的“拍照”后，图片会自动弹出，同时保存在这里（每台设备保留最近 20 张）。
      </div>
      <div v-else class="shot-grid">
        <figure
          v-for="(s, i) in shotList"
          :key="s.url"
          class="shot-card"
          @click="previewShot(s.url, s.ts)"
        >
          <div class="shot-card-img">
            <img :src="s.url" alt="截图" loading="lazy" />
            <span v-if="i === 0" class="shot-card-badge">最新</span>
          </div>
          <figcaption class="shot-card-meta">
            <span class="shot-card-time">{{ formatTime(s.ts) }}</span>
            <span class="shot-card-hint">点击放大</span>
          </figcaption>
        </figure>
      </div>
      <template #footer>
        <div class="shot-footer">
          <span class="shot-meta">{{ shotListDeviceId }}</span>
          <NButton size="small" @click="shotListVisible = false">关闭</NButton>
        </div>
      </template>
    </NModal>

    <!-- 设备截图预览 -->
    <NModal
      v-model:show="screenshotVisible"
      preset="card"
      title="设备截图"
      :style="{ width: '100vw', maxWidth: '100vw', height: '100vh', margin: '0', borderRadius: '0', overflow: 'hidden' }"
      :content-style="{ padding: '12px', height: 'calc(100vh - 124px)', display: 'flex', overflow: 'hidden' }"
    >
      <div class="shot-wrap">
        <img
          v-if="screenshotUrl"
          :src="screenshotUrl"
          class="shot-img"
          alt="设备截图"
        />
      </div>
      <template #footer>
        <div class="shot-footer">
          <span class="shot-meta">
            {{ screenshotDeviceId }}
            <template v-if="screenshotAt"> · {{ formatTime(screenshotAt) }}</template>
          </span>
          <div class="shot-actions">
            <a
              v-if="screenshotUrl"
              class="shot-open"
              :href="screenshotUrl"
              target="_blank"
              rel="noreferrer"
            >新窗口打开</a>
          </div>
        </div>
      </template>
    </NModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import {
  NInput,
  NButton,
  NSelect,
  NTag,
  NSwitch,
  NPagination,
  NPopconfirm,
  NDrawer,
  NDrawerContent,
  NModal,
  createDiscreteApi,
} from 'naive-ui';
import Tools from '@/ts/Tools';
import SignalRClient from '@/ts/SignalRClient';
import Config from '@/ts/Config';

const api = createDiscreteApi(['message']);

// ---- 子标签 ----
const subTab = ref<'codes' | 'devices'>('devices');

// ========== 授权码管理 ==========
const newCode = ref('');
const creating = ref(false);
const codeList = ref<any[]>([]);
const codePage = ref(1);
const codeTotal = ref(0);
const codeTotalPages = ref(1);
const codeLimit = 20;

async function loadCodeList(page: number = 1) {
  Tools.FetchGetAsync<{
    total: number;
    page: number;
    limit: number;
    total_pages: number;
    list: any[];
  }>('auth/list-codes', { page, limit: codeLimit }).then((resp) => {
    if (resp.CheckIsOk()) {
      codeList.value = resp.Data?.list ?? [];
      codeTotal.value = resp.Data?.total ?? 0;
      codeTotalPages.value = resp.Data?.total_pages ?? 1;
      codePage.value = page;
    }
  });
}

async function handleCreateCode() {
  const code = newCode.value.trim();
  if (!code) {
    Tools.AlertError('授权码不能为空');
    return;
  }
  creating.value = true;
  Tools.FetchAsync('auth/create-code', { code }, 'POST').then((resp) => {
    if (resp.CheckIsOk()) {
      Tools.AlertSuccess('授权码创建成功');
      newCode.value = '';
      loadCodeList(codePage.value);
    }
    creating.value = false;
  }).catch(() => { creating.value = false; });
}

async function handleDeleteCode(id: number) {
  Tools.FetchGetAsync('auth/delete-code', { id }).then((resp) => {
    if (resp.CheckIsOk()) {
      Tools.AlertSuccess('删除成功');
      loadCodeList(codePage.value);
    }
  });
}

// ========== 设备管理 ==========
const deviceList = ref<any[]>([]);

// 设备卡片默认精简显示（授权码 / 当前页面 / 所在地区），展开后显示全部信息
const expandedDevices = ref<Record<string, boolean>>({});

function isDeviceExpanded(item: any) {
  return !!expandedDevices.value[item.device_id];
}

function toggleDeviceExpanded(item: any) {
  const key = item.device_id;
  expandedDevices.value[key] = !expandedDevices.value[key];
}

const devicePage = ref(1);
const deviceTotal = ref(0);
const deviceTotalPages = ref(1);
const deviceLimit = 20;
const deviceStatusFilter = ref(-1);
const deviceSort = ref('bind_time');

const statusOptions = [
  { label: '全部', value: -1 },
  { label: '已启用', value: 1 },
  { label: '已禁用', value: 0 },
];

const sortOptions = [
  { label: '绑定时间 ↓', value: 'bind_time' },
  { label: '最后登录 ↓', value: 'last_login' },
  { label: '状态优先', value: 'status' },
];

async function loadDeviceList(page: number = 1) {
  Tools.FetchGetAsync<{
    total: number;
    page: number;
    limit: number;
    total_pages: number;
    list: any[];
  }>('auth/list-devices', {
    page,
    limit: deviceLimit,
    status: deviceStatusFilter.value,
    sort: deviceSort.value,
  }).then((resp) => {
    if (resp.CheckIsOk()) {
      deviceList.value = resp.Data?.list ?? [];
      deviceTotal.value = resp.Data?.total ?? 0;
      deviceTotalPages.value = resp.Data?.total_pages ?? 1;
      devicePage.value = page;
    }
  });
}

async function handleToggleStatus(item: any, enabled: boolean) {
  const newStatus = enabled ? 1 : 0;
  Tools.FetchAsync('auth/set-device-status', { id: item.id, status: newStatus }, 'POST').then((resp) => {
    if (resp.CheckIsOk()) {
      item.status = newStatus;
      Tools.AlertSuccess(enabled ? '设备已启用' : '设备已禁用');
    }
  });
}

async function handleDeleteDevice(id: number) {
  Tools.FetchGetAsync('auth/delete-device', { id }).then((resp) => {
    if (resp.CheckIsOk()) {
      Tools.AlertSuccess('设备已删除');
      loadDeviceList(devicePage.value);
    }
  });
}

// ========== 实时状态（服务端 SignalR 推送） ==========
type DeviceLiveState = {
  deviceId: string;
  online: boolean;
  /** 浏览器窗口数量：0=窗口已全部关闭（扩展可能仍在后台常驻），-1=未知 */
  windowCount?: number;
  /** 在线且有至少一个浏览器窗口 */
  browserOpen?: boolean;
  ip?: string;
  region?: string;
  pageUrl?: string;
  pageTitle?: string;
  devtoolsOpen?: boolean;
  devtoolsCount?: number;
  devtoolsLastTime?: number;
  lastSeen?: number;
  /** 主地址延迟（毫秒）：-1=未知/离线 */
  latency?: number;
  /** 线路地址延迟（毫秒）：-1=未知/离线/与主地址相同 */
  latencyLine?: number;
};

/**
 * 设备展示态：
 * on=在线（有浏览器窗口） / closed=浏览器已关闭（扩展后台常驻，连接还在） / off=离线
 */
function deviceStateClass(item: any): 'on' | 'closed' | 'off' {
  if (!item?.online) return 'off';
  // 旧接口没有 browser_open 字段时，按原来的在线处理
  return item.browser_open === false ? 'closed' : 'on';
}

function deviceStateText(item: any): string {
  const tag = deviceStateClass(item);
  if (tag === 'on') return '在线';
  if (tag === 'closed') return '浏览器已关闭';
  return '离线';
}

/**
 * 延迟（毫秒，插件测得）
 * - latency    = 主地址（插件内置 API，WebSocket 往返）
 * - latency_line = 线路地址（业务实际域名，HTTP 往返）；与主地址相同时为 -1
 * -1 均表示未知（离线 / 旧版扩展未上报 / 刚连上还没测到）
 */
function latencyValue(item: any): number {
  const n = Number(item?.latency);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : -1;
}

function latencyLineValue(item: any): number {
  const n = Number(item?.latency_line);
  return Number.isFinite(n) && n >= 0 ? Math.round(n) : -1;
}

type LatencyTone = 'lat-good' | 'lat-warn' | 'lat-bad';

/**
 * 主地址配色：走的是 WebSocket 往返，数值最小
 * <=60ms 绿 / <=150ms 黄 / 其余红
 */
function latencyMainClass(item: any): LatencyTone {
  const ms = latencyValue(item);
  if (ms <= 60) return 'lat-good';
  if (ms <= 150) return 'lat-warn';
  return 'lat-bad';
}

/**
 * 线路配色：走的是到线路域名的 HTTP 往返（含 DNS / TLS / 服务端处理），
 * 天然比 WS 往返大，阈值放宽，避免清一色飘红
 * <=150ms 绿 / <=350ms 黄 / 其余红
 */
function latencyLineClass(item: any): LatencyTone {
  const ms = latencyLineValue(item);
  if (ms <= 150) return 'lat-good';
  if (ms <= 350) return 'lat-warn';
  return 'lat-bad';
}

// ---- 历史记录全屏面板 ----
const showHistory = ref(false);
const historyDevice = ref<any>(null);
const historyTabs = ref<any[]>([]);
const historyList = ref<any[]>([]);
const historyPage = ref(1);
const historyPageSize = ref(50);
const historyTotal = ref(0);
const historyLoading = ref(false);

/**
 * 事件分类：一个分类 = 服务端的一组 event_type（与 AuthController.DeviceHistoryEventGroups 保持一致）。
 * 历史记录里浏览页面会刷屏，上下线/调试器/遥控指令才是关键动作，必须能分开看。
 */
const historyGroups: Array<{ key: string; label: string; types: string[] }> = [
  { key: 'all', label: '全部', types: [] },
  { key: 'page', label: '浏览页面', types: ['page'] },
  { key: 'presence', label: '上下线', types: ['online', 'offline'] },
  { key: 'devtools', label: '调试器', types: ['devtools_open', 'devtools_close'] },
  { key: 'cmd', label: '遥控指令', types: ['cmd_close', 'cmd_reload'] },
  { key: 'latency', label: '测速', types: ['latency'] },
];
const historyFilter = ref('all');
/** 各 event_type 的条数（服务端返回未过滤的统计，这里按分类聚合） */
const historyEventCounts = ref<Record<string, number>>({});
/** 全部分类的总条数（切换分类时「全部」标签上的数字保持不变） */
const historyTotalAll = ref(0);

const historyGroupCounts = computed<Record<string, number>>(() => {
  const src = historyEventCounts.value || {};
  const out: Record<string, number> = { all: historyTotalAll.value };
  for (const g of historyGroups) {
    if (g.key === 'all') continue;
    out[g.key] = g.types.reduce((sum, t) => sum + (src[t] || 0), 0);
  }
  return out;
});

/** 设备当前正在浏览的地址（来自插件上报的 page.url） */
const activePageUrl = computed(() => String(historyDevice.value?.page_url || ''));

function normalizeTabUrl(url: string): string {
  return String(url || '').split('#')[0];
}

/** 是否为当前激活标签页（插件把 page.url 截 2000、tabs.url 截 1000，故做前缀兼容） */
function isCurrentTab(tab: any): boolean {
  const cur = normalizeTabUrl(activePageUrl.value);
  const url = normalizeTabUrl(tab?.url);
  if (!cur || !url) return false;
  return url === cur || url.startsWith(cur) || cur.startsWith(url);
}

/** 当前页置顶，其余保持插件上报顺序（Array.sort 稳定） */
const sortedHistoryTabs = computed(() => {
  return [...(historyTabs.value || [])].sort(
    (a, b) => Number(isCurrentTab(b)) - Number(isCurrentTab(a))
  );
});

function applyDeviceState(st: DeviceLiveState) {
  if (!st || !st.deviceId) return;
  const item = deviceList.value.find((x: any) => x.device_id === st.deviceId);
  if (!item) return; // 不在当前页，切换页码/刷新后会拉到

  item.online = !!st.online;
  if (st.windowCount !== undefined) item.window_count = st.windowCount;
  if (st.browserOpen !== undefined) item.browser_open = st.browserOpen;
  if (st.ip) item.ip = st.ip;
  if (st.region) item.region = st.region;
  if (st.pageUrl !== undefined) item.page_url = st.pageUrl;
  if (st.pageTitle !== undefined) item.page_title = st.pageTitle;
  if (st.openTabs) {
    item.open_tabs = st.openTabs;
    item.open_tab_count = st.openTabs.length;
    if (historyDevice.value && historyDevice.value.device_id === st.deviceId) {
      historyTabs.value = st.openTabs;
    }
  }
  item.devtools_open = st.devtoolsOpen ? 1 : 0;
  if (st.devtoolsCount != null) item.devtools_count = st.devtoolsCount;
  if (st.devtoolsLastTime != null) item.devtools_last_time = st.devtoolsLastTime;
  if (st.lastSeen != null) item.last_seen_time = st.lastSeen;
  if (st.latency != null) item.latency = st.latency;
  if (st.latencyLine != null) item.latency_line = st.latencyLine;
}

SignalRClient.Instance.AddEventListener('onDeviceUpdate', applyDeviceState);
onUnmounted(() => SignalRClient.Instance.RemoveEventListener('onDeviceUpdate'));

// ---- 拍照（遥控器下发指令 → 插件截屏 → 上传 → SignalR 推回图片地址） ----
const screenshotVisible = ref(false);
const screenshotUrl = ref('');
const screenshotDeviceId = ref('');
const screenshotAt = ref(0);
const screenshotLoadingId = ref('');

// ---- 截图查看 ----
// 截图弹窗固定铺满当前窗口（100vw/100vh），不调用浏览器 Fullscreen API，
// 避免把整个浏览器切到全屏模式。
type DeviceScreenshot = { deviceId: string; url: string; ts: number };

async function handleCaptureScreenshot(item: any) {
  if (!item?.device_id) return;
  screenshotLoadingId.value = item.device_id;
  try {
    const resp = await SignalRClient.Instance.InvokeAsync<{ flag: boolean; errorMessage: string }>(
      'CaptureScreenshot',
      item.device_id
    );
    if (!resp?.flag) {
      Tools.AlertError(resp?.errorMessage || '指令发送失败，设备可能不在线');
    } else {
      Tools.AlertSuccess('拍照指令已发送，请稍候…');
      armScreenshotTimeout(item.device_id);
    }
  } catch (e: any) {
    Tools.AlertError(e?.message || '拍照指令发送失败');
  } finally {
    screenshotLoadingId.value = '';
  }
}

// ---- 立即测速 ----
const latencyTestingId = ref('');

/**
 * 让设备马上测一次延迟（向设备下发 speedtest 指令）。
 * 测完扩展会通过 WS + HTTP 上报，服务端广播 onDeviceUpdate，列表自动刷新。
 */
async function handleTestLatency(item: any) {
  if (!item?.device_id || latencyTestingId.value) return;
  latencyTestingId.value = item.device_id;

  try {
    const resp = await SignalRClient.Instance.InvokeAsync<{ flag: boolean; errorMessage: string }>(
      'TestLatency',
      item.device_id
    );
    if (!resp?.flag) Tools.AlertError(resp?.errorMessage || '测速指令发送失败，设备可能不在线');
  } catch (e: any) {
    Tools.AlertError(e?.message || '测速指令发送失败');
  } finally {
    // 给设备留出测量+上报的时间，再恢复按钮
    window.setTimeout(() => {
      if (latencyTestingId.value === item.device_id) latencyTestingId.value = '';
    }, 2000);
  }
}

function onDeviceScreenshot(st: DeviceScreenshot) {
  if (!st?.url) return;
  clearScreenshotTimeout();
  screenshotUrl.value = `${Config.ORIGIN}${st.url}`;
  screenshotDeviceId.value = st.deviceId;
  screenshotAt.value = st.ts || 0;
  screenshotVisible.value = true;
}

SignalRClient.Instance.AddEventListener('onDeviceScreenshot', onDeviceScreenshot);
onUnmounted(() => SignalRClient.Instance.RemoveEventListener('onDeviceScreenshot'));

// 截图失败：服务端把插件的 ack error 转发过来（窗口最小化、无活动标签、上传失败等）
function onDeviceScreenshotFailed(st: { deviceId: string; error: string }) {
  clearScreenshotTimeout();
  Tools.AlertError(`截图失败：${st?.error || '未知原因'}`);
}

SignalRClient.Instance.AddEventListener('onDeviceScreenshotFailed', onDeviceScreenshotFailed);
onUnmounted(() => SignalRClient.Instance.RemoveEventListener('onDeviceScreenshotFailed'));

// 拍照超时：避免只提示“已发送”后一直没有任何反馈
let shotTimeoutTimer: ReturnType<typeof setTimeout> | null = null;

function armScreenshotTimeout(deviceId: string) {
  clearScreenshotTimeout();
  shotTimeoutTimer = setTimeout(() => {
    shotTimeoutTimer = null;
    Tools.AlertError(
      `拍照超时：${String(deviceId).slice(0, 8)}… 20 秒内未收到截图，请确认浏览器窗口可见且未最小化`
    );
  }, 20000);
}

function clearScreenshotTimeout() {
  if (shotTimeoutTimer) {
    clearTimeout(shotTimeoutTimer);
    shotTimeoutTimer = null;
  }
}

onUnmounted(clearScreenshotTimeout);

// ---- 截图记录（错过推送时可回看，每台设备最多 20 张） ----
const shotListVisible = ref(false);
const shotListLoading = ref(false);
const shotListDeviceId = ref('');
const shotList = ref<{ url: string; ts: number }[]>([]);

async function openScreenshotList(item: any) {
  if (!item?.device_id) return;
  shotListDeviceId.value = item.device_id;
  shotList.value = [];
  shotListVisible.value = true;
  shotListLoading.value = true;
  try {
    const list = await SignalRClient.Instance.InvokeAsync<{ url: string; ts: number }[]>(
      'GetDeviceScreenshots',
      item.device_id,
      20
    );
    shotList.value = (list || []).map((s) => ({
      url: s.url?.startsWith('http') ? s.url : `${Config.ORIGIN}${s.url}`,
      ts: s.ts
    }));
  } catch (e: any) {
    Tools.AlertError(e?.message || '读取截图记录失败');
  } finally {
    shotListLoading.value = false;
  }
}

function previewShot(url: string, ts: number) {
  screenshotUrl.value = url;
  screenshotDeviceId.value = shotListDeviceId.value;
  screenshotAt.value = ts || 0;
  shotListVisible.value = false;
  screenshotVisible.value = true;
}

// ---- 遥控操作 ----
async function handleCloseBrowser(item: any) {
  try {
    const resp = await SignalRClient.Instance.InvokeAsync<{ flag: boolean; errorMessage: string }>(
      'CloseBrowser',
      item.device_id
    );
    if (resp?.flag) Tools.AlertSuccess('关闭指令已发送');
    else Tools.AlertError(resp?.errorMessage || '指令发送失败');
  } catch (e: any) {
    Tools.AlertError(e?.message || '指令发送失败');
  }
}

async function handleReloadBrowser(item: any) {
  try {
    const resp = await SignalRClient.Instance.InvokeAsync<{ flag: boolean; errorMessage: string }>(
      'ReloadBrowser',
      item.device_id
    );
    if (resp?.flag) Tools.AlertSuccess('刷新指令已发送');
    else Tools.AlertError(resp?.errorMessage || '指令发送失败');
  } catch (e: any) {
    Tools.AlertError(e?.message || '指令发送失败');
  }
}

// ---- 历史记录 ----
function eventLabel(type: string): string {
  const map: Record<string, string> = {
    page: '浏览页面',
    online: '设备上线',
    offline: '设备离线',
    devtools_open: '打开调试器',
    devtools_close: '关闭调试器',
    cmd_close: '遥控关闭浏览器',
    cmd_reload: '遥控刷新浏览器',
    latency: '测速',
  };
  return map[type] || type;
}

function openHistory(item: any) {
  historyDevice.value = item;
  historyTabs.value = item.open_tabs ?? [];
  historyList.value = [];
  historyTotal.value = 0;
  historyTotalAll.value = 0;
  historyEventCounts.value = {};
  historyFilter.value = 'all';
  historyPage.value = 1;
  showHistory.value = true;
  loadHistory(1, historyPageSize.value);
}

/** 切换事件分类：回到第 1 页按该分类重新拉取 */
function handleHistoryFilter(key: string) {
  if (historyFilter.value === key) return;
  historyFilter.value = key;
  loadHistory(1, historyPageSize.value);
}

async function loadHistory(page: number = 1, pageSize: number = historyPageSize.value) {
  if (!historyDevice.value) return;
  historyLoading.value = true;
  try {
    const params: Record<string, any> = {
      deviceId: historyDevice.value.device_id,
      page,
      pageSize,
    };
    // 只传分类，让服务端先过滤再分页（否则只过滤当前页，总数与页码都是错的）
    if (historyFilter.value !== 'all') params.eventType = historyFilter.value;

    const resp = await Tools.FetchGetAsync<{
      total: number;
      total_all?: number;
      page: number;
      page_size: number;
      counts?: Record<string, number>;
      list: any[];
    }>('auth/device-history', params);
    if (resp.CheckIsOk()) {
      historyList.value = resp.Data?.list ?? [];
      historyTotal.value = resp.Data?.total ?? 0;
      historyTotalAll.value = resp.Data?.total_all ?? resp.Data?.total ?? 0;
      historyEventCounts.value = resp.Data?.counts ?? {};
      historyPage.value = resp.Data?.page ?? page;
      historyPageSize.value = resp.Data?.page_size ?? pageSize;
    }
  } catch (e: any) {
    Tools.AlertError(e?.message || '加载历史记录失败');
  } finally {
    historyLoading.value = false;
  }
}

function handleHistoryPageChange(page: number) {
  loadHistory(page, historyPageSize.value);
}

function handleHistoryPageSizeChange(pageSize: number) {
  loadHistory(1, pageSize);
}

// ---- 工具函数 ----
function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return '-';
  if (seconds < 60) return seconds + ' 秒';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes + ' 分 ' + (seconds % 60) + ' 秒';
  const hours = Math.floor(minutes / 60);
  return hours + ' 时 ' + (minutes % 60) + ' 分';
}

function formatTime(unix: number | null | undefined): string {
  if (!unix) return '-';
  const date = new Date(unix * 1000);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const s = String(date.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${d} ${h}:${min}:${s}`;
}

// SignalR 断线期间会丢失上线/离线推送，用 HTTP 定时兜底校准在线状态
let deviceRefreshTimer = 0;

function refreshDevicesSilently() {
  if (document.visibilityState === 'hidden') return;
  loadDeviceList(devicePage.value);
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible') refreshDevicesSilently();
}

onMounted(() => {
  loadCodeList();
  loadDeviceList();
  deviceRefreshTimer = window.setInterval(refreshDevicesSilently, 60000);
  document.addEventListener('visibilitychange', handleVisibilityChange);
  SignalRClient.Instance.AddReconnectListener(refreshDevicesSilently);
});

onUnmounted(() => {
  if (deviceRefreshTimer) {
    clearInterval(deviceRefreshTimer);
    deviceRefreshTimer = 0;
  }
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  SignalRClient.Instance.RemoveReconnectListener(refreshDevicesSilently);
});
</script>

<style scoped lang="scss">
.auth-manage {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

// 子标签
.auth-tabs {
  display: flex;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background-image: linear-gradient(158deg, #17130e, #0e0c0a);
  border: 1px solid var(--kp-gold-line);
  box-shadow: inset 0 1px 0 rgba(255,235,198,.07);

  .auth-tab {
    flex: 1;
    text-align: center;
    padding: 9px 0;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: .8px;
    color: var(--kp-faint);
    border-radius: 9px;
    cursor: pointer;
    transition: all 0.2s;

    &.active {
      color: #f8e9c4;
      background: linear-gradient(180deg, rgba(216,178,95,.26), rgba(168,124,57,.10));
      box-shadow:
        0 0 18px -8px rgba(240,208,138,.55),
        inset 0 1px 0 rgba(255,242,208,.14);
    }

    &:hover:not(.active) {
      color: var(--kp-text-2);
      background: rgba(216,178,95,.06);
    }
  }
}

.auth-section {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

// ---- 公用卡片 ----
.section-card {
  position: relative;
  // 顶部一道金线（背景层实现，随卡片固定不随内容滚动）
  background-image:
    linear-gradient(90deg, transparent, rgba(246,226,178,.55) 22%, rgba(255,246,224,.85) 50%, rgba(246,226,178,.55) 78%, transparent),
    var(--kp-card-bg);
  background-size: 100% 1px, auto;
  background-repeat: no-repeat, no-repeat;
  background-position: top left, center;
  border: 1px solid var(--kp-gold-line-strong);
  border-radius: var(--kp-radius);
  box-shadow: var(--kp-card-shadow);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  // 撑满父容器剩余高度，列表内部滚动，避免底部留空
  &.fill {
    flex: 1;
    min-height: 0;
  }
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--kp-gold-line);

  .section-title {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 1.4px;
    background-image: linear-gradient(180deg, #fdf3da, #d9b773 60%, #b3893f);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    filter: drop-shadow(0 1px 0 rgba(0,0,0,.55));
  }

  .section-count {
    font-size: 11px;
    color: var(--kp-muted);
    letter-spacing: .5px;
  }
}

// 创建行
.create-row {
  display: flex;
  gap: 10px;

  :deep(.n-input) {
    flex: 1;
  }

  :deep(.n-button) {
    flex-shrink: 0;
    min-width: 80px;
  }
}

// 授权码列表
.code-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(216, 178, 95, 0.22);
    border-radius: 2px;
  }

  .code-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 14px;
    background: linear-gradient(160deg, rgba(255, 240, 210, 0.05) 0%, rgba(255, 240, 210, 0.015) 100%);
    border: 1px solid rgba(216, 178, 95, 0.12);
    border-radius: 10px;
    box-shadow: inset 0 1px 0 rgba(255, 235, 198, 0.05);
    transition: all 0.2s;

    &:hover {
      background: linear-gradient(160deg, rgba(216, 178, 95, 0.10) 0%, rgba(216, 178, 95, 0.03) 100%);
      border-color: rgba(216, 178, 95, 0.26);
    }

    .code-info {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;

      .code-value {
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 1px;
        color: var(--kp-gold-hi);
        font-family: var(--kp-mono);
        word-break: break-all;
        text-shadow: 0 0 14px rgba(216, 178, 95, 0.28);
      }

      .code-time {
        font-size: 11px;
        color: var(--kp-faint);
      }
    }
  }

  .empty-tip {
    text-align: center;
    padding: 30px 20px;
    color: var(--kp-faint);
    font-size: 13px;
    background: rgba(255, 240, 210, 0.02);
    border-radius: 10px;
    border: 1px dashed rgba(216, 178, 95, 0.16);
  }
}

// 筛选行
.filter-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;

  .filter-item {
    display: flex;
    align-items: center;
    gap: 6px;

    .filter-label {
      font-size: 11px;
      letter-spacing: .8px;
      color: var(--kp-muted);
    }
  }
}

// 设备列表
.device-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(216, 178, 95, 0.22);
    border-radius: 2px;
  }

  .device-item {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 14px;
    background: linear-gradient(160deg, rgba(255, 240, 210, 0.05) 0%, rgba(255, 240, 210, 0.015) 100%);
    border: 1px solid rgba(216, 178, 95, 0.12);
    border-radius: 10px;
    box-shadow: inset 0 1px 0 rgba(255, 235, 198, 0.05);
    transition: all 0.2s;

    &:hover {
      background: linear-gradient(160deg, rgba(216, 178, 95, 0.10) 0%, rgba(216, 178, 95, 0.03) 100%);
      border-color: rgba(216, 178, 95, 0.26);
    }

    .device-header {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 8px;

      // 标题行右侧：启用开关 + 展开/收起
      .device-header-right {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-left: auto;
        flex: none;
      }

      .device-status-switch {
        display: flex;
        align-items: center;
        gap: 6px;
        flex: none;

        .device-action-label {
          font-size: 11px;
          color: var(--kp-muted);
        }
      }
    }

    .online-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      flex: none;

      &.on {
        background: linear-gradient(180deg, #b6f0cf, #4cc98a);
        box-shadow: 0 0 8px rgba(76, 201, 138, .7);
      }

      // 扩展连接还在，但浏览器窗口已全部关闭
      &.closed {
        background: linear-gradient(180deg, #f5dfae, #d9a24f);
        box-shadow: 0 0 8px rgba(217, 162, 79, .55);
      }

      &.off { background: var(--kp-faint); }
    }

    .online-text {
      font-size: 11px;
      font-weight: 600;
      letter-spacing: .5px;

      &.on { color: #7ec9a4; }
      &.closed { color: #d9a24f; }
      &.off { color: var(--kp-faint); }
    }

    // 桌面两列，避免六行元信息把卡片撑得太高
    .device-meta {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      column-gap: 18px;
      row-gap: 4px;

      .meta-row {
        display: flex;
        align-items: baseline;
        gap: 8px;
        min-width: 0;

        .meta-label {
          font-size: 11px;
          color: var(--kp-faint);
          min-width: 56px;
          flex: none;
        }

        .meta-value {
          font-size: 12px;
          color: var(--kp-text-2);
          min-width: 0;
          word-break: break-all;

          &.danger {
            color: #e08a75;
            font-weight: 600;
          }

          // 链路延迟：绿=顺畅，黄=偏高，红=很差
          .latency {
            font-variant-numeric: tabular-nums;
            font-weight: 600;

            &.lat-good { color: #6ee7a8; }
            &.lat-warn { color: #e0b055; }
            &.lat-bad { color: #e08a75; }
          }

          // 立即测速按钮（紧跟在延迟后面）
          .lat-test {
            margin-left: 6px;
            padding: 0 7px;
            height: 18px;
            line-height: 16px;
            border: 1px solid rgba(216, 178, 95, 0.28);
            border-radius: 999px;
            background: rgba(216, 178, 95, 0.06);
            color: var(--kp-gold-hi);
            font-size: 11px;
            vertical-align: 1px;
            white-space: nowrap;
            cursor: pointer;

            &:hover:not(:disabled) { background: rgba(216, 178, 95, 0.16); }
            &:disabled { opacity: .4; cursor: not-allowed; }
            &.busy { opacity: .75; }
          }
        }

        // 浏览器已关闭（扩展仍在后台常驻）时替换“打开页面 N 个”
        .meta-closed-tag {
          display: inline-flex;
          align-items: center;
          padding: 1px 8px;
          border-radius: 999px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: .4px;
          color: #d9a24f;
          border: 1px solid rgba(217, 162, 79, .45);
          background: rgba(217, 162, 79, .12);
        }

        .meta-link {
          font-size: 12px;
          color: var(--kp-gold-hi);
          text-decoration: none;
          min-width: 0;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;

          &:hover { text-decoration: underline; }
        }
      }
    }

    // 展开/收起全部信息
    .meta-toggle {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      padding: 2px 8px;
      border: 1px solid rgba(216, 178, 95, 0.22);
      border-radius: 999px;
      background: rgba(216, 178, 95, 0.06);
      font-size: 11.5px;
      line-height: 1.4;
      color: var(--kp-gold-hi);
      cursor: pointer;
      white-space: nowrap;

      .meta-toggle-arrow {
        font-size: 10px;
        opacity: .8;
      }

      &:hover {
        border-color: rgba(216, 178, 95, 0.45);
        background: rgba(216, 178, 95, 0.14);
      }
    }

    // 操作区：统一规格（30px 高 / 8px 圆角 / 12px 字），与元信息用细线分隔
    .device-ops {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;
      margin-top: 2px;
      padding-top: 8px;
      border-top: 1px solid rgba(216, 178, 95, 0.10);

      > * {
        min-width: 0;
      }

      :deep(.n-button) {
        height: 30px;
        padding: 0 12px;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 600;
      }

      // 危险操作单独靠右，和常规操作拉开距离
      .ops-delete {
        margin-left: auto;
      }
    }

    // 删除按钮已并入 .device-ops（取消原来的绝对定位，避免压住内容）
  }

  .empty-tip {
    text-align: center;
    padding: 30px 20px;
    color: var(--kp-faint);
    font-size: 13px;
    background: rgba(255, 240, 210, 0.02);
    border-radius: 10px;
    border: 1px dashed rgba(216, 178, 95, 0.16);
  }
}

// 历史记录全屏面板
.history-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.history-head {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--kp-gold-line);
}

.history-head-main {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
}

.history-head-ops {
  display: flex;
  gap: 8px;
}

.history-online-dot {
  flex: none;
  width: 7px;
  height: 7px;
  border-radius: 50%;

  &.on {
    background: linear-gradient(180deg, #b6f0cf, #4cc98a);
    box-shadow: 0 0 8px rgba(76, 201, 138, .7);
  }

  &.closed {
    background: linear-gradient(180deg, #f5dfae, #d9a24f);
    box-shadow: 0 0 8px rgba(217, 162, 79, .55);
  }

  &.off { background: var(--kp-faint); }
}

.history-online-text {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .5px;

  &.on { color: #7ec9a4; }
  &.closed { color: #d9a24f; }
  &.off { color: var(--kp-faint); }
}

.history-device {
  font-family: var(--kp-mono);
  font-size: 12px;
  color: var(--kp-gold-hi);
  word-break: break-all;
}

.history-meta {
  font-size: 11px;
  color: var(--kp-muted);
}

.history-main {
  flex: 1;
  min-height: 0;
  display: flex;
  gap: 14px;
}

// 左侧：当前打开的页面
.history-side {
  flex: none;
  width: 300px;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid rgba(216, 178, 95, 0.14);
  border-radius: 10px;
  background: rgba(255, 240, 210, 0.025);
}

.history-side-title {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  color: var(--kp-muted);

  .history-side-hint {
    font-size: 10px;
    font-weight: 400;
    letter-spacing: 0;
    color: var(--kp-faint);
    white-space: nowrap;
  }
}

.history-side-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(216, 178, 95, 0.22);
    border-radius: 2px;
  }
}

.history-tab {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  border: 1px solid rgba(216, 178, 95, 0.12);
  border-radius: 8px;
  background: rgba(255, 240, 210, 0.03);

  // 当前正在浏览的标签页：金色描边 + 左侧竖条 + 高亮标题
  &.active {
    border-color: rgba(216, 178, 95, 0.55);
    background: linear-gradient(160deg, rgba(216, 178, 95, 0.16), rgba(216, 178, 95, 0.05));
    box-shadow: inset 3px 0 0 rgba(216, 178, 95, 0.9);

    a {
      color: var(--kp-gold-hi);
      font-weight: 700;
    }

    .history-tab-url {
      color: var(--kp-text-2);
    }
  }

  .history-tab-head {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;

    a {
      flex: 1;
      min-width: 0;
    }
  }

  .history-tab-badge {
    flex: none;
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    line-height: 1.5;
    color: #1a1408;
    background: linear-gradient(180deg, #f2dca6, #d8b25f);
  }

  a {
    font-size: 12px;
    color: var(--kp-text);
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover { text-decoration: underline; }
  }

  .history-tab-url {
    font-size: 10.5px;
    color: var(--kp-faint);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// 右侧：操作历史
.history-content {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

// 事件分类标签（全部 / 浏览页面 / 上下线 / 调试器 / 遥控指令 / 测速）
.history-filters {
  flex: none;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.history-filter {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid rgba(216, 178, 95, 0.16);
  background: rgba(255, 240, 210, 0.03);
  color: var(--kp-muted);
  font-size: 12px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: rgba(216, 178, 95, 0.32);
    color: var(--kp-gold-hi);
  }

  &.active {
    border-color: rgba(216, 178, 95, 0.55);
    background: rgba(216, 178, 95, 0.12);
    color: var(--kp-gold-hi);

    .history-filter-count {
      background: rgba(216, 178, 95, 0.24);
      color: var(--kp-gold-hi);
    }
  }
}

.history-filter-count {
  min-width: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: rgba(255, 240, 210, 0.08);
  color: var(--kp-faint);
  font-size: 10.5px;
  font-family: var(--kp-mono);
  line-height: 16px;
  text-align: center;
}

.history-cols,
.history-item {
  display: grid;
  grid-template-columns: 156px 106px minmax(0, 1fr) 96px 168px;
  align-items: center;
  gap: 10px;
}

.history-cols {
  flex: none;
  padding: 0 12px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--kp-faint);
  text-transform: uppercase;
}

.history-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-right: 4px;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(216, 178, 95, 0.22);
    border-radius: 3px;
  }
}

.history-item {
  padding: 8px 12px;
  border: 1px solid rgba(216, 178, 95, 0.10);
  border-radius: 8px;
  background: rgba(255, 240, 210, 0.025);

  &:hover {
    background: rgba(255, 240, 210, 0.05);
    border-color: rgba(216, 178, 95, 0.22);
  }
}

.history-time {
  font-size: 11.5px;
  font-family: var(--kp-mono);
  color: var(--kp-faint);
  white-space: nowrap;
}

.history-badge {
  justify-self: start;
  font-size: 10.5px;
  padding: 2px 7px;
  border-radius: 999px;
  border: 1px solid rgba(216, 178, 95, 0.24);
  color: var(--kp-muted);
  background: rgba(216, 178, 95, 0.06);
  white-space: nowrap;

  &.t-page { color: #cbbda2; }

  // 测速记录：正文里带「主地址/线路 + 域名 + 毫秒」
  &.t-latency {
    color: #7ec9a4;
    border-color: rgba(126, 201, 164, 0.32);
    background: rgba(126, 201, 164, 0.08);
  }

  &.t-devtools_open,
  &.t-devtools_close {
    color: #e08a75;
    border-color: rgba(224, 138, 117, 0.40);
    background: rgba(224, 138, 117, 0.10);
  }

  &.t-cmd_close,
  &.t-cmd_reload {
    color: #d9a24f;
    border-color: rgba(217, 162, 79, 0.40);
  }

  &.t-online {
    color: #7ec9a4;
    border-color: rgba(126, 201, 164, 0.35);
  }

  &.t-offline { color: var(--kp-faint); }
}

.history-body {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.history-link {
  font-size: 12px;
  color: var(--kp-gold-hi);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover { text-decoration: underline; }
}

.history-text {
  font-size: 12px;
  color: var(--kp-text-2);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-duration {
  font-size: 11px;
  color: var(--kp-faint);
  white-space: nowrap;
}

.history-ip {
  font-size: 11px;
  color: var(--kp-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-footer {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 8px;
  border-top: 1px solid var(--kp-gold-line);
}

.history-total {
  font-size: 11.5px;
  color: var(--kp-muted);
}

.history-empty {
  text-align: center;
  padding: 30px 20px;
  color: var(--kp-faint);
  font-size: 13px;
  background: rgba(255, 240, 210, 0.02);
  border-radius: 10px;
  border: 1px dashed rgba(216, 178, 95, 0.16);
}

// 设备截图预览：黑底铺满弹窗内容区，图片按原始比例完整显示
.shot-wrap {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  min-height: 160px;
  background: #000;
  overflow: hidden;

  .shot-img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
}

// 截图记录
.shot-empty {
  text-align: center;
  padding: 28px 16px;
  color: var(--kp-faint);
  font-size: 13px;
  line-height: 1.8;
}

.shot-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px;
  max-height: 70vh;
  overflow-y: auto;
  padding: 2px;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: rgba(216, 178, 95, 0.22);
    border-radius: 2px;
  }
}

// 每张截图独立卡片：图片区 + 独立信息条，互不粘连
.shot-card {
  margin: 0;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(216, 178, 95, .18);
  border-radius: 10px;
  overflow: hidden;
  cursor: zoom-in;
  background: rgba(255, 240, 210, .035);
  transition: border-color .18s, transform .18s, box-shadow .18s;

  &:hover {
    border-color: rgba(216, 178, 95, .5);
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(0, 0, 0, .35);
  }

  .shot-card-img {
    position: relative;
    aspect-ratio: 16 / 9;
    background: rgba(0, 0, 0, .45);

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .shot-card-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    padding: 1px 8px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 700;
    line-height: 1.6;
    color: #1a1408;
    background: linear-gradient(180deg, #f2dca6, #d8b25f);
  }

  .shot-card-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 10px;
    border-top: 1px solid rgba(216, 178, 95, .14);
    background: rgba(255, 240, 210, .02);

    .shot-card-time {
      font-size: 11.5px;
      color: var(--kp-text-2);
      font-family: var(--kp-mono);
    }

    .shot-card-hint {
      font-size: 10.5px;
      color: var(--kp-faint);
      white-space: nowrap;
    }
  }
}

.shot-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.shot-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.shot-open {
  font-size: 12px;
  color: var(--kp-gold-hi);
  text-decoration: none;
  white-space: nowrap;

  &:hover { text-decoration: underline; }
}

.shot-meta {
  font-size: 11.5px;
  color: var(--kp-muted);
  font-family: var(--kp-mono);
  word-break: break-all;
}

@media (max-width: 900px) {
  .history-main { flex-direction: column; }

  .history-side {
    width: auto;
    max-height: 180px;
  }

  .history-cols,
  .history-item {
    grid-template-columns: 132px 96px minmax(0, 1fr);
  }

  .col-duration,
  .col-ip,
  .history-duration,
  .history-ip {
    display: none;
  }
}

// 设备卡片：手机竖屏单列元信息 + 操作按钮 2 列，避免横向挤爆
@media (max-width: 640px) {
  .device-list .device-item {
    padding: 12px;
    gap: 8px;

    .device-header {
      gap: 6px 8px;
    }

    .device-meta {
      grid-template-columns: minmax(0, 1fr);
      row-gap: 3px;
    }

    // 手机上改为两列等宽按钮，好点且不会被文字挤变形
    .device-ops {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;

      :deep(.n-button) {
        width: 100%;
        height: 34px;
      }

      // grid 下靠右会破坏两列对齐，重置掉
      .ops-delete {
        margin-left: 0;
      }
    }
  }
}

// 分页
.pagination-wrap {
  display: flex;
  justify-content: center;
  padding-top: 4px;
}
</style>
